import { collator, formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'

const sortByPosition = (aRow, bRow, dir) => dir * (aRow.position - bRow.position)

const formatOneDecimal = value => (Number.isFinite(value) && value.toFixed(1)) || value

const formatAmountsOrValue = value => (Number.isFinite(value) && formatAmounts(value)) || value

const sortByCellValue = index => (aRow, bRow, dir) =>
  dir * (aRow.cells[index].value + aRow.position / 10000 - (bRow.cells[index].value + bRow.position / 10000))

// The TV sort travels inside bpm<key> as an `s<order>` token, in the vocabulary the coffee list
// used: the column it names (i entities/attributes, s score, r responses) then the direction.
// Each sortable column carries its own letter, so the click handler need not know the layout.
const SORT_DIRS = { asc: 1, desc: -1 }

const sortedBy = (sortOrder, token) => {
  const order = String(sortOrder || '')
  const dir = SORT_DIRS[order.slice(1)]

  return dir && token === order.charAt(0) ? { sorted: dir } : {}
}

export const sortOrderOf = (token, dir) => `${token}${1 === dir ? 'asc' : 'desc'}`

// The most-associated cell holds a list of names rather than a number. A pair that reads as
// numeric is ordered as numbers, anything else by the collator, both with the same position
// tiebreak the other columns use.
const sortByAssociated = index => (aRow, bRow, dir) => {
  const aValue = aRow.cells[index].value
  const bValue = bRow.cells[index].value

  const numeric = !Number.isNaN(Number(aValue)) && !Number.isNaN(Number(bValue))
  const [a, b] = numeric
    ? [Number(aValue), Number(bValue)]
    : [collator.compare(aValue, bValue), collator.compare(bValue, aValue)]

  return dir * (a + aRow.position / 100000 - (b + bRow.position / 100000))
}

const responsesAppendix = responses => `${formatAmounts(responses)} ${pluralize(responses, 'response', 'responses')}`

const isBaVersion = answer => answer?.mds?.version !== undefined && 3 <= answer.mds.version

const resolvePosition = ({ tvElevationsRepresentValue, defaultValues }) => {
  let position = Number(tvElevationsRepresentValue)
  if (Number.isNaN(position)) position = defaultValues.e.a
  return position
}

// characterization: coffee builds this identical smoothed-score aggregation in both the
// "all attributes" and "all entities" variants — factored here, behavior unchanged.
const computeSmoothScores = answer => {
  const smoothScores = {}
  const tempAttr = {}

  if (answer?.mds?.scoresSmoothed) {
    const smoothBrands = answer.mds.scoresSmoothed
    smoothBrands.forEach((brand, index) => {
      smoothScores[`qv${index + 1}`] = brand.reduce((a, b) => a + b) / brand.length
      brand.forEach((attr, attrIndex) => {
        tempAttr[`b${attrIndex + 1}`] ||= []
        tempAttr[`b${attrIndex + 1}`].push(attr)
      })
    })
    Object.keys(tempAttr).forEach(index => {
      smoothScores[index] = tempAttr[index].reduce((a, b) => a + b) / smoothBrands.length
    })
  }

  return smoothScores
}

export const calcAttrItemsListProps = ({
  id,
  answer,
  questionVariants,
  batches,
  tvBrandsValue,
  sortOrder,
  ...positionProps
}) => {
  const isLoading = 'loading' === answer
  const responses = answer?.total || 0
  const meanList = answer?.meanList || null

  const position = resolvePosition(positionProps)
  const b = batches[position - 1]

  const cols = [
    { type: 'title', title: processText(b.text), sort: sortByPosition, sortToken: 'i', ...sortedBy(sortOrder, 'i') },
    {
      type: 'value',
      title: 'Score',
      width: 520,
      appendix: responsesAppendix(responses),
      sort: sortByCellValue(1),
      sortToken: 's',
      ...sortedBy(sortOrder, 's'),
      format: formatOneDecimal
    }
  ]

  const rows = questionVariants.map(qv => {
    const qvId = `qv${qv.position}`
    const row = {
      cells: [
        { title: `${qv.position}. ${processText(qv.text)}` },
        { value: '', loading: isLoading, classNames: ['left'] }
      ],
      disabled: 0 < tvBrandsValue.length && !tvBrandsValue.includes(String(qv.position)),
      position: qv.position
    }

    if (Object.hasOwn(qv, 'image')) row.cells[0].image = qv.image

    if (meanList) {
      // characterization: coffee's `responses?` guard is always true (responses is seeded `|| 0`);
      // the raw-answer-without-total branch is unreachable, so only this path is ported.
      row.cells[1].value = meanList[qvId]?.[`b${position}`] || 'n/a'
      row.cells[1].total = answer[qvId]?.[`b${position}`]?.total || 0
    }

    return row
  })

  return { id, cols, rows, classNames: ['plain'] }
}

export const calcAttrAllItemsListProps = ({
  id,
  answer,
  questionVariants,
  batches,
  tvAttributesValue,
  showResponses,
  sortOrder
}) => {
  const isLoading = 'loading' === answer
  const responses = answer?.total || 0
  const smoothScores = computeSmoothScores(answer)

  const cols = [
    { type: 'title', title: 'Attributes', sort: sortByPosition, sortToken: 'i', ...sortedBy(sortOrder, 'i') },
    {
      type: 'value',
      title: isBaVersion(answer) ? 'BA Scores' : 'Score',
      width: 100,
      sort: sortByCellValue(1),
      sortToken: 's',
      ...sortedBy(sortOrder, 's'),
      format: formatOneDecimal
    },
    {
      type: 'value',
      title: 'Most associated entities',
      width: showResponses ? 310 : 410,
      appendix: responsesAppendix(responses),
      sort: sortByAssociated(showResponses ? 3 : 2),
      sortToken: 'q',
      ...sortedBy(sortOrder, 'q'),
      format: formatOneDecimal
    }
  ]

  const rows = batches.map(b => {
    const bId = `b${b.position}`
    const row = {
      cells: [
        { title: `${b.position}. ${processText(b.text)}` },
        { value: '', loading: isLoading },
        { value: '', loading: isLoading, classNames: ['left'] }
      ],
      disabled: 0 < tvAttributesValue.length && !tvAttributesValue.includes(String(b.position)),
      position: b.position
    }

    if (Object.hasOwn(b, 'image')) row.cells[0].image = b.image

    row.cells[1].value =
      0 < Object.keys(smoothScores).length ? smoothScores[bId] || '' : answer?.meanStat?.attr?.mean?.[bId] || ''

    if (answer?.mds?.dist?.attrs[bId] !== undefined) {
      row.cells[2].value = answer.mds.dist.attrs[bId]
        .reduce((acc, cur) => {
          if (cur[1] > 0.33 * answer.mds.dist.max) return acc

          const qv = questionVariants[Number(cur[0].slice(2)) - 1]
          acc.push(`<b>${processText(qv.text)}</b>`)

          return acc
        }, [])
        .join(', ')
    }

    if (showResponses) {
      row.cells.splice(2, 0, { value: answer?.qv1?.[bId]?.total || '', loading: isLoading })
    }

    return row
  })

  if (showResponses) {
    cols.splice(2, 0, {
      type: 'value',
      title: 'Responses',
      width: 100,
      sort: sortByCellValue(2),
      sortToken: 'r',
      ...sortedBy(sortOrder, 'r'),
      format: formatAmountsOrValue
    })
  }

  return { id, cols, rows, classNames: ['plain'] }
}

export const calcBrItemsListProps = ({
  id,
  answer,
  questionVariants,
  batches,
  tvAttributesValue,
  sortOrder,
  ...positionProps
}) => {
  const isLoading = 'loading' === answer
  const responses = answer?.total || 0
  const meanList = answer?.meanList || null

  const position = resolvePosition(positionProps)
  const qv = questionVariants[position - 1]

  const cols = [
    { type: 'title', title: processText(qv.text), sort: sortByPosition, sortToken: 'i', ...sortedBy(sortOrder, 'i') },
    {
      type: 'value',
      title: 'Score',
      width: 520,
      appendix: responsesAppendix(responses),
      sort: sortByCellValue(1),
      sortToken: 's',
      ...sortedBy(sortOrder, 's'),
      format: formatOneDecimal
    }
  ]

  if (Object.hasOwn(qv, 'image')) cols[0].image = qv.image

  const rows = batches.map(b => {
    const bId = `b${b.position}`
    const row = {
      cells: [
        { title: `${b.position}. ${processText(b.text)}` },
        { value: '', loading: isLoading, classNames: ['left'] }
      ],
      disabled: 0 < tvAttributesValue.length && !tvAttributesValue.includes(String(b.position)),
      position: b.position
    }

    if (meanList) {
      // characterization: same always-true `responses?` guard as calcAttrItemsListProps.
      row.cells[1].value = meanList[`qv${position}`]?.[bId] || 'n/a'
      row.cells[1].total = answer[`qv${position}`]?.[bId]?.total || 0
    }

    return row
  })

  return { id, cols, rows, classNames: ['plain'] }
}

export const calcBrAllItemsListProps = ({
  id,
  answer,
  questionVariants,
  batches,
  tvBrandsValue,
  showResponses,
  sortOrder
}) => {
  const isLoading = 'loading' === answer
  const responses = answer?.total || 0
  const smoothScores = computeSmoothScores(answer)

  const cols = [
    { type: 'title', title: 'Entities', sort: sortByPosition, sortToken: 'i', ...sortedBy(sortOrder, 'i') },
    {
      type: 'value',
      width: 100,
      title: isBaVersion(answer) ? 'BA Scores' : 'Score',
      sort: sortByCellValue(1),
      sortToken: 's',
      ...sortedBy(sortOrder, 's'),
      format: formatOneDecimal
    },
    {
      type: 'value',
      title: 'Most associated attributes',
      width: showResponses ? 310 : 410,
      appendix: responsesAppendix(responses),
      sort: sortByAssociated(showResponses ? 3 : 2),
      sortToken: 'q',
      ...sortedBy(sortOrder, 'q'),
      format: formatOneDecimal
    }
  ]

  const rows = questionVariants.map(qv => {
    const qvId = `qv${qv.position}`
    const row = {
      cells: [
        { title: `${qv.position}. ${processText(qv.text)}` },
        { value: '', loading: isLoading },
        { value: '', loading: isLoading, classNames: ['left'] }
      ],
      disabled: 0 < tvBrandsValue.length && !tvBrandsValue.includes(String(qv.position)),
      position: qv.position
    }

    if (Object.hasOwn(qv, 'image')) row.cells[0].image = qv.image

    row.cells[1].value =
      0 < Object.keys(smoothScores).length ? smoothScores[qvId] || '' : answer?.meanStat?.brand?.mean?.[qvId] || ''

    if (answer?.mds?.dist?.brands[qvId] !== undefined) {
      row.cells[2].value = answer.mds.dist.brands[qvId]
        .reduce((acc, cur) => {
          if (cur[1] > 0.33 * answer.mds.dist.max) return acc

          const b = batches[Number(cur[0].slice(1)) - 1]
          acc.push(`<b>${processText(b.text)}</b>`)

          return acc
        }, [])
        .join(', ')
    }

    if (showResponses) {
      row.cells.splice(2, 0, { value: answer?.[qvId]?.total || '', loading: isLoading })
    }

    return row
  })

  if (showResponses) {
    cols.splice(2, 0, {
      type: 'value',
      title: 'Responses',
      width: 110,
      sort: sortByCellValue(2),
      sortToken: 'r',
      ...sortedBy(sortOrder, 'r'),
      format: formatAmountsOrValue
    })
  }

  return { id, cols, rows, classNames: ['plain'] }
}

export const calcTvItemsListProps = props => {
  const { tvPlotByValue, tvElevationsRepresentValue, defaultValues } = props
  let position = Number(tvElevationsRepresentValue)

  if ('e' === tvPlotByValue) {
    if (Number.isNaN(position)) position = defaultValues.e.e
    return -1 === position ? calcBrAllItemsListProps(props) : calcBrItemsListProps(props)
  }

  if ('a' === tvPlotByValue) {
    if (Number.isNaN(position)) position = defaultValues.e.a
    return -2 === position ? calcAttrAllItemsListProps(props) : calcAttrItemsListProps(props)
  }

  return calcBrAllItemsListProps(props)
}
